import { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { useAppSelector } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import {
  adminSections,
  reflectorV1AdminSections,
  reflectorV2AdminSections,
  filterAdminSectionsByPermission,
  type AdminSection,
} from "../components/AdminSections";
import {
  filterSubscriptionsAdminSection,
  getFirstAvailableAdminRoute,
} from "../sideMenuVisibility";
import { isProductionEnvironment } from "../sideMenuEnv";
import { isEmailInAdminCenterWhitelist } from "../sideMenuAdminWhitelist";
import { ProductInternalName } from "../productConfig";

export type AdminCenterAccess = {
  /** GLOBAL_ADMIN or the email whitelist — the rule, on its own. */
  canAccessAdminCenter: boolean;
  /** The active product's sections, already permission-filtered. */
  activeAdminSections: AdminSection[];
  /**
   * First route this user can actually open, or null when the sections filtered
   * down to nothing openable.
   */
  firstAdminRoute: string | null;
  /**
   * Whether an "Admin center" row should render at all. Stated rather than
   * inferred from an empty panel, and it covers the one case `canAccess` does
   * not: a whitelisted email with no admin permissions, who passes the gate and
   * still has no section to open.
   */
  showAdminCenterRow: boolean;
};

/**
 * Who may reach the Admin center, and where it lands.
 *
 * Both the rail's account card and the header's account control need this
 * answer, and CTX-635/636 put them in different trees — so the derivation lives
 * here and is imported, rather than being written out twice and drifting the
 * day someone edits one of them. Same rule as `filterAccountAreaItems`: one
 * source of truth per gate.
 *
 * @param internalName Active product. Passed in rather than read from Redux so
 * the rail can hand over the value `useSideMenuProductSelection` resolved this
 * render — Redux trails it by a dispatch, and a caller that mixed the two would
 * show one product's Admin center while the nav showed another's.
 */
export const useAdminCenterAccess = (
  internalName: string
): AdminCenterAccess => {
  const { user } = useAuth0();
  const { TenantType: tenantType, ListPermiso: userPermissions } =
    useAppSelector((state: RootState) => state.userProfile);

  const canAccessAdminCenter = useMemo(
    () =>
      tenantType === "GLOBAL_ADMIN" || isEmailInAdminCenterWhitelist(user?.email),
    [tenantType, user?.email]
  );

  const filteredAdminSections = useMemo(() => {
    const byPermission = filterAdminSectionsByPermission(
      adminSections,
      userPermissions || []
    );
    // Subscriptions carries no `requiredPermission`, so it survives the
    // permission filter by design and is gated on tenant type instead — see
    // `filterSubscriptionsAdminSection` for why it can't reuse the convention.
    return filterSubscriptionsAdminSection(
      byPermission,
      tenantType,
      isProductionEnvironment()
    );
  }, [userPermissions, tenantType]);

  const activeAdminSections = useMemo(() => {
    if (internalName === ProductInternalName.CtxReflectorV2) {
      return reflectorV2AdminSections;
    }
    if (internalName === ProductInternalName.CtxReflector) {
      return reflectorV1AdminSections;
    }
    return filteredAdminSections;
  }, [internalName, filteredAdminSections]);

  const firstAdminRoute = useMemo(
    () => getFirstAvailableAdminRoute(activeAdminSections),
    [activeAdminSections]
  );

  return {
    canAccessAdminCenter,
    activeAdminSections,
    firstAdminRoute,
    showAdminCenterRow: canAccessAdminCenter && firstAdminRoute !== null,
  };
};
